import React, { useState, useEffect, useRef } from 'react';
import { MessageCircle, Heart, Share2, Plus, Search, TrendingUp, Users, Clock, MapPin, User, Mail, Eye, BarChart3, ExternalLink, ThumbsUp } from 'lucide-react';
import { Post, Comment } from '../types';
import { showToast } from '../utils/toast';
import { DiscussionView } from './Discussion';

interface CommunityEchoesProps {
  posts: Post[];
  onCreatePost: (content: string, category: string, isAnonymous: boolean, postType?: string, expertTag?: string, pollOptions?: string[]) => void;
  onPostInteraction: (postId: string, action: string) => void;
  onFollowExpert: (expertId: string) => void;
  navigationData?: any;
  currentUser?: any | null;
  handleAuthRequired?: (message: string, mode?: 'signin' | 'signup') => void;
}

const CommunityEchoes: React.FC<CommunityEchoesProps> = ({
  posts,
  onCreatePost,
  onPostInteraction,
  onFollowExpert,
  navigationData,
  currentUser,
  handleAuthRequired
}) => {
  const [activeFilter, setActiveFilter] = useState('all');
  const [showCreatePost, setShowCreatePost] = useState(false);
  const [selectedPost, setSelectedPost] = useState<Post | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [isWellnessCircle, setIsWellnessCircle] = useState(false);
  const [selectedUser, setSelectedUser] = useState<any>(null);
  const [expandedPosts, setExpandedPosts] = useState<Set<string>>(new Set());
  const [showDiscussion, setShowDiscussion] = useState<string | null>(null);
  const [userVotes, setUserVotes] = useState<{[postId: string]: number}>({});

  const categories = ['all', 'general', 'mental-health', 'nutrition', 'fitness', 'chronic-conditions'];
  const experts = ['Dr. Ama Osei', 'Dr. Kwaku Mensah', 'Dr. Akosua Frimpong'];

  useEffect(() => {
    if (navigationData) {
      if (navigationData.searchQuery) {
        setSearchTerm(navigationData.searchQuery);
      }
      
      if (navigationData.openPostDetails && navigationData.postId) {
        const post = posts.find(p => p.id === navigationData.postId);
        if (post) {
          setShowDiscussion(post.id);
        }
      }

      // Handle pre-filled post creation from drug dictionary
      if (navigationData.createPost) {
        setShowCreatePost(true);
      }
    }
  }, [navigationData, posts]);

  const filteredPosts = posts
    .filter(post => activeFilter === 'all' || post.category === activeFilter)
    .filter(post => 
      post.content.toLowerCase().includes(searchTerm.toLowerCase()) ||
      post.authorName.toLowerCase().includes(searchTerm.toLowerCase())
    )
    .filter(post => {
      if (isWellnessCircle) {
        return post.authorName.includes('Kumasi') || post.content.includes('Kumasi') || Math.random() > 0.5;
      }
      return true;
    });

  // Handle auth-required actions
  const handleCreatePostClick = () => {
    if (!currentUser && handleAuthRequired) {
      handleAuthRequired('Please sign in to create a post', 'signup');
      return;
    }
    setShowCreatePost(true);
  };

  const handlePostDetailClick = (post: Post) => {
    setShowDiscussion(post.id);
  };

  const handleFollowClick = (expertId: string) => {
    if (!currentUser && handleAuthRequired) {
      handleAuthRequired('Please sign in to follow experts', 'signup');
      return;
    }
    onFollowExpert(expertId);
  };

  const handleUserProfileClick = (user: any) => {
    if (!currentUser && handleAuthRequired) {
      handleAuthRequired('Please sign in to view user profiles', 'signin');
      return;
    }
    setSelectedUser(user);
  };

  const handleSharePost = async (post: Post) => {
    const shareData = {
      title: `${post.authorName}'s post on PharmPlus`,
      text: post.content.substring(0, 100) + '...',
      url: window.location.href + `?post=${post.id}`
    };

    if (navigator.share) {
      try {
        await navigator.share(shareData);
        showToast.community.postShared();
      } catch (err) {
        console.log('Error sharing:', err);
      }
    } else {
      // Fallback to copying to clipboard
      try {
        await navigator.clipboard.writeText(`${shareData.text} - ${shareData.url}`);
        showToast.system.success('Post link copied to clipboard!');
      } catch (err) {
        // Fallback for older browsers
        const textArea = document.createElement('textarea');
        textArea.value = `${shareData.text} - ${shareData.url}`;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
        showToast.system.success('Post link copied to clipboard!');
      }
    }
  };

  const handleVoteOnPoll = (postId: string, optionIndex: number) => {
    if (!currentUser && handleAuthRequired) {
      handleAuthRequired('Please sign in to vote on polls', 'signup');
      return;
    }
    
    // Update local state immediately for better UX
    setUserVotes(prev => ({
      ...prev,
      [postId]: optionIndex
    }));
    
    // Call the API to record the vote
    onPostInteraction(postId, `vote:${optionIndex}`);
  };

  const togglePostExpansion = (postId: string) => {
    setExpandedPosts(prev => {
      const newSet = new Set(prev);
      if (newSet.has(postId)) {
        newSet.delete(postId);
      } else {
        newSet.add(postId);
      }
      return newSet;
    });
  };

  // Handle backdrop clicks for modal dismissal
  const handleModalBackdropClick = (e: React.MouseEvent, closeModal: () => void) => {
    if (e.target === e.currentTarget) {
      closeModal();
    }
  };

  const CreatePostModal = () => {
    const [title, setTitle] = useState('');
    const [content, setContent] = useState(navigationData?.content || '');
    const [category, setCategory] = useState(navigationData?.category || 'general');
    const [isAnonymous, setIsAnonymous] = useState(navigationData?.isAnonymous || false);
    const [postType, setPostType] = useState(navigationData?.postType || 'text');
    const [taggedExpert, setTaggedExpert] = useState(navigationData?.expertTag || '');
    const [showExpertDropdown, setShowExpertDropdown] = useState(false);
    const [pollOptions, setPollOptions] = useState(['', '']);

    const handleSubmit = (e: React.FormEvent) => {
      e.preventDefault();
      if (content.trim()) {
        let finalContent = content;
        
        // Add title if provided
        if (title.trim()) {
          finalContent = `**${title}**\n\n${content}`;
        }
        
        // For polls, we'll handle the options separately in the backend
        const finalPollOptions = postType === 'poll' ? pollOptions.filter(option => option.trim()) : [];
        
        onCreatePost(
          finalContent, 
          category, 
          isAnonymous, 
          postType, 
          taggedExpert,
          finalPollOptions
        );
        
        // Reset form
        setTitle('');
        setContent('');
        setPollOptions(['', '']);
        setShowCreatePost(false);
      }
    };

    const handleExpertTag = (expert: string) => {
      setTaggedExpert(expert);
      setContent(prev => prev + ` @${expert}`);
      setShowExpertDropdown(false);
    };

    const addPollOption = () => {
      if (pollOptions.length < 5) {
        setPollOptions([...pollOptions, '']);
      }
    };

    const updatePollOption = (index: number, value: string) => {
      const newOptions = [...pollOptions];
      newOptions[index] = value;
      setPollOptions(newOptions);
    };

    const removePollOption = (index: number) => {
      if (pollOptions.length > 2) {
        setPollOptions(pollOptions.filter((_, i) => i !== index));
      }
    };

    return (
      <div 
        className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fade-in"
        onClick={(e) => handleModalBackdropClick(e, () => setShowCreatePost(false))}
      >
        <div className="bg-white dark:bg-gray-900 rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto animate-scale-in scrollbar-custom" onClick={e => e.stopPropagation()}>
          <div className="p-4 border-b border-gray-200 dark:border-gray-700">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Share with Community</h3>
            <p className="text-gray-600 dark:text-gray-300 text-sm">Choose your post type and connect with others</p>
          </div>
          
          <form onSubmit={handleSubmit} className="p-4 space-y-4">
            {/* Post Type Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Post Type</label>
              <div className="grid grid-cols-3 gap-2">
                {[
                  { id: 'text', label: 'Text', icon: '📝' },
                  { id: 'poll', label: 'Poll', icon: '📊' },
                  { id: 'question', label: 'Question', icon: '❓' }
                ].map((type, index) => (
                  <button
                    key={type.id}
                    type="button"
                    onClick={() => setPostType(type.id)}
                    className={`p-3 border-2 rounded-lg transition-all duration-300 text-center hover:scale-105 ${
                      postType === type.id
                        ? 'border-green-500 bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-400 scale-105'
                        : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                    }`}
                    style={{ animationDelay: `${index * 50}ms` }}
                  >
                    <div className="text-lg mb-1">{type.icon}</div>
                    <div className="text-xs font-medium">{type.label}</div>
                  </button>
                ))}
              </div>
            </div>

            {/* Title Input */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Title (Optional)
              </label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Give your post a catchy title..."
                className="input-base dark:bg-gray-800 dark:border-gray-700"
              />
            </div>

            {/* Category Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Category</label>
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                className="select-base dark:bg-gray-800 dark:border-gray-700"
              >
                {categories.slice(1).map(cat => (
                  <option key={cat} value={cat}>
                    {cat.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}
                  </option>
                ))}
              </select>
            </div>
            
            {/* Content Input */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {postType === 'question' ? 'Your Question' : 
                 postType === 'poll' ? 'Poll Question' : 'Your Message'}
              </label>
              <textarea
                value={content}
                onChange={(e) => setContent(e.target.value)}
                rows={4}
                placeholder={
                  postType === 'question' ? 'Ask your health question...' :
                  postType === 'poll' ? 'What would you like to poll about?' :
                  'Share your thoughts or experience...'
                }
                className="textarea-base dark:bg-gray-800 dark:border-gray-700"
                required
              />
            </div>

            {/* Poll Options */}
            {postType === 'poll' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Poll Options</label>
                <div className="space-y-2">
                  {pollOptions.map((option, index) => (
                    <div key={index} className="flex items-center space-x-2">
                      <input
                        type="text"
                        value={option}
                        onChange={(e) => updatePollOption(index, e.target.value)}
                        placeholder={`Option ${index + 1}`}
                        className="input-base flex-1 dark:bg-gray-800 dark:border-gray-700"
                        required
                      />
                      {pollOptions.length > 2 && (
                        <button
                          type="button"
                          onClick={() => removePollOption(index)}
                          className="text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 p-1"
                        >
                          ×
                        </button>
                      )}
                    </div>
                  ))}
                  {pollOptions.length < 5 && (
                    <button
                      type="button"
                      onClick={addPollOption}
                      className="text-green-600 dark:text-green-400 hover:text-green-700 dark:hover:text-green-300 text-sm font-medium"
                    >
                      + Add Option
                    </button>
                  )}
                </div>
              </div>
            )}

            {/* Expert Tagging */}
            <div className="relative">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Tag an Expert (Optional)</label>
              <button
                type="button"
                onClick={() => setShowExpertDropdown(!showExpertDropdown)}
                className="input-base text-left hover:bg-gray-50 dark:bg-gray-800 dark:border-gray-700 dark:hover:bg-gray-750 transition-all duration-200 hover:scale-102"
              >
                {taggedExpert ? `@${taggedExpert}` : 'Select an expert to tag...'}
              </button>
              
              {showExpertDropdown && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg z-10 animate-slide-up">
                  {experts.map((expert, index) => (
                    <button
                      key={expert}
                      type="button"
                      onClick={() => handleExpertTag(expert)}
                      className="w-full p-3 text-left hover:bg-gray-50 dark:hover:bg-gray-750 transition-all duration-200 border-b border-gray-100 dark:border-gray-700 last:border-b-0 hover:scale-102"
                      style={{ animationDelay: `${index * 50}ms` }}
                    >
                      <div className="flex items-center space-x-2">
                        <div className="avatar-sm">
                          {expert.charAt(0)}
                        </div>
                        <div>
                          <p className="font-medium text-gray-900 dark:text-gray-100">{expert}</p>
                          <p className="text-xs text-gray-600 dark:text-gray-400">Verified Expert</p>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
            
            {/* Anonymous Option */}
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="anonymous"
                checked={isAnonymous}
                onChange={(e) => setIsAnonymous(e.target.checked)}
                className="w-4 h-4 text-green-600 border-gray-300 dark:border-gray-600 rounded focus:ring-green-500 transition-all duration-200"
              />
              <label htmlFor="anonymous" className="text-sm text-gray-700 dark:text-gray-300">
                Post anonymously
              </label>
            </div>
            
            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={() => setShowCreatePost(false)}
                className="btn-ghost dark:text-gray-300"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn-primary"
              >
                Share {postType === 'question' ? 'Question' : postType === 'poll' ? 'Poll' : 'Post'}
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  };

  const PollDisplay = ({ post }: { post: Post }) => {
    const userVote = userVotes[post.id] !== undefined 
      ? userVotes[post.id] 
      : post.userVote;
    
    const totalVotes = post.pollVotesCount 
      ? post.pollVotesCount.reduce((sum, count) => sum + count, 0) 
      : 0;
    
    return (
      <div className="mt-3 space-y-2">
        <h4 className="font-medium text-gray-900 dark:text-gray-100 flex items-center space-x-2">
          <BarChart3 size={16} className="text-blue-600 dark:text-blue-400" />
          <span>Poll</span>
          <span className="text-sm text-gray-500 dark:text-gray-400">({totalVotes} votes)</span>
        </h4>
        
        <div className="space-y-2">
          {post.pollOptions?.map((option, index) => {
            const voteCount = post.pollVotesCount?.[index] || 0;
            const votePercentage = totalVotes > 0 ? Math.round((voteCount / totalVotes) * 100) : 0;
            const isSelected = userVote === index;
            
            return (
              <button
                key={index}
                onClick={() => handleVoteOnPoll(post.id, index)}
                disabled={userVote !== undefined}
                className={`w-full p-3 rounded-lg text-left relative overflow-hidden transition-all duration-200 ${
                  isSelected 
                    ? 'bg-green-100 dark:bg-green-900/50 border border-green-300 dark:border-green-700' 
                    : userVote !== undefined
                    ? 'bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700'
                    : 'bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-750'
                }`}
              >
                {/* Background progress bar */}
                <div 
                  className={`absolute top-0 left-0 bottom-0 ${
                    isSelected ? 'bg-green-200 dark:bg-green-800/50' : 'bg-gray-200 dark:bg-gray-700/50'
                  }`}
                  style={{ width: `${votePercentage}%` }}
                />
                
                {/* Option content */}
                <div className="relative flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <div className={`w-5 h-5 rounded-full flex items-center justify-center ${
                      isSelected 
                        ? 'bg-green-500 text-white' 
                        : 'border border-gray-300 dark:border-gray-600'
                    }`}>
                      {isSelected && <ThumbsUp size={12} />}
                    </div>
                    <span className="dark:text-gray-200">{option}</span>
                  </div>
                  <span className="font-medium dark:text-gray-200">{votePercentage}%</span>
                </div>
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  const UserProfilePopover = ({ user }: { user: any }) => (
    <div 
      className="fixed inset-0 bg-black/30 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fade-in"
      onClick={(e) => handleModalBackdropClick(e, () => setSelectedUser(null))}
    >
      <div className="card-base card-padding-md w-full max-w-sm animate-scale-in dark:bg-gray-900 dark:border-gray-700" onClick={e => e.stopPropagation()}>
        <div className="p-4">
          <div className="flex items-center space-x-3 mb-4">
            <div className="avatar-xl hover:scale-110 transition-transform duration-300">
              {user.name.charAt(0)}
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 dark:text-gray-100">{user.name}</h3>
              {user.isExpert && (
                <span className="badge-success animate-pulse">
                  ✓ Verified Expert
                </span>
              )}
              <p className="text-gray-600 dark:text-gray-400 text-sm">Member since 2023</p>
            </div>
          </div>
          
          <div className="grid grid-cols-3 gap-4 mb-4 text-center">
            <div className="hover:scale-105 transition-transform duration-200">
              <p className="font-bold text-gray-900 dark:text-gray-100">24</p>
              <p className="text-xs text-gray-600 dark:text-gray-400">Posts</p>
            </div>
            <div className="hover:scale-105 transition-transform duration-200">
              <p className="font-bold text-gray-900 dark:text-gray-100">156</p>
              <p className="text-xs text-gray-600 dark:text-gray-400">Helpful</p>
            </div>
            <div className="hover:scale-105 transition-transform duration-200">
              <p className="font-bold text-gray-900 dark:text-gray-100">89</p>
              <p className="text-xs text-gray-600 dark:text-gray-400">Followers</p>
            </div>
          </div>
          
          <div className="space-y-2">
            <button
              onClick={() => {
                handleFollowClick(user.id);
                setSelectedUser(null);
              }}
              className="btn-primary w-full"
            >
              Follow
            </button>
            <div className="grid grid-cols-2 gap-2">
              <button 
                onClick={() => {
                  onNavigate('profile', { openChat: true, userId: user.id });
                  setSelectedUser(null);
                }}
                className="btn-ghost flex items-center justify-center space-x-1 dark:text-gray-300"
              >
                <Mail size={16} />
                <span>Message</span>
              </button>
              <button 
                onClick={() => {
                  onNavigate('profile', { viewProfile: true, userId: user.id });
                  setSelectedUser(null);
                }}
                className="btn-ghost flex items-center justify-center space-x-1 dark:text-gray-300"
              >
                <Eye size={16} />
                <span>Profile</span>
              </button>
            </div>
          </div>
        </div>
        
        <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded-b-xl">
          <button
            onClick={() => setSelectedUser(null)}
            className="w-full text-center text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 transition-colors duration-200 text-sm"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );

  const ReactionButton = ({ type, count, active, postId }: { type: string; count: number; active: boolean; postId: string }) => {
    const getEmoji = (type: string) => {
      switch (type) {
        case 'helpful': return '💪';
        case 'heart': return '❤️';
        case 'thanks': return '🙏';
        default: return '👍';
      }
    };

    return (
      <button
        onClick={() => onPostInteraction(postId, type)}
        className={`flex items-center space-x-1 px-2 py-1 rounded-full text-sm transition-all duration-200 hover:scale-105 ${
          active 
            ? 'bg-green-100 dark:bg-green-900/50 text-green-700 dark:text-green-300 animate-bounce-gentle' 
            : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-750'
        }`}
      >
        <span>{getEmoji(type)}</span>
        <span className="font-medium">{count}</span>
      </button>
    );
  };

  const truncateContent = (content: string, maxLines: number = 3) => {
    const words = content.split(' ');
    const wordsPerLine = 15; // Approximate words per line
    const maxWords = maxLines * wordsPerLine;
    
    if (words.length <= maxWords) {
      return content;
    }
    
    return words.slice(0, maxWords).join(' ') + '...';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-pink-50 to-purple-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 pb-20 lg:pb-0 animate-fade-in">
      {/* Header */}
      <div className="glass border-b border-gray-200 dark:bg-gray-900/90 dark:border-gray-800 sticky top-0 z-30 animate-slide-up">
        <div className="container-responsive py-4">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-4">
              <div className="animate-slide-up">
                <h1 className="text-xl font-bold text-gray-900 dark:text-gray-100">Community</h1>
                <p className="text-gray-600 dark:text-gray-300 text-sm">
                  {currentUser ? 'Connect and share experiences' : 'Preview community discussions'}
                </p>
              </div>
              
              {/* Wellness Circle Toggle */}
              <div className="flex items-center space-x-2 animate-slide-up">
                <MapPin size={16} className={`transition-colors duration-200 ${isWellnessCircle ? 'text-green-600 dark:text-green-400' : 'text-gray-400 dark:text-gray-500'}`} />
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={isWellnessCircle}
                    onChange={(e) => setIsWellnessCircle(e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 dark:bg-gray-700 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-green-300 dark:peer-focus:ring-green-800 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-600"></div>
                </label>
                <span className="text-sm text-gray-700 dark:text-gray-300">
                  {isWellnessCircle ? 'Kumasi Circle' : 'Global'}
                </span>
              </div>
            </div>
            
            {/* New Post Button */}
            <button
              onClick={handleCreatePostClick}
              className="btn-primary flex items-center space-x-2 animate-float"
            >
              <Plus size={16} />
              <span>New Post</span>
              {!currentUser && (
                <span className="badge-base bg-green-700 text-white">Sign In</span>
              )}
            </button>
          </div>
          
          {/* Search and Filters */}
          <div className="flex flex-col sm:flex-row gap-3 animate-fade-in" style={{ animationDelay: '200ms' }}>
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 transition-colors duration-200" size={16} />
              <input
                type="text"
                placeholder="Search discussions..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="input-base pl-9 dark:bg-gray-800 dark:border-gray-700"
              />
            </div>
            
            <div className="flex space-x-2 overflow-x-auto scrollbar-custom">
              {categories.map((category, index) => (
                <button
                  key={category}
                  onClick={() => setActiveFilter(category)}
                  className={`px-3 py-2 rounded-lg font-medium whitespace-nowrap transition-all duration-200 text-sm ${
                    activeFilter === category
                      ? 'bg-green-600 text-white dark:bg-green-700 dark:text-white shadow-lg'
                      : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-750'
                  }`}
                  style={{ animationDelay: `${index * 50}ms` }}
                >
                  {category === 'all' ? 'All' : category.split('-').map(word => 
                    word.charAt(0).toUpperCase() + word.slice(1)
                  ).join(' ')}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Posts Feed */}
      <div className="container-responsive py-6">
        {!currentUser && (
          <div className="bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800 rounded-xl p-4 mb-6 animate-slide-up">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center text-white">
                <Eye size={20} />
              </div>
              <div className="flex-1">
                <h3 className="font-medium text-blue-900 dark:text-blue-100">Community Preview</h3>
                <p className="text-blue-700 dark:text-blue-300 text-sm">You're viewing a preview of our community discussions. Sign in to join the conversation!</p>
              </div>
            </div>
          </div>
        )}

        <div className="space-y-4">
          {filteredPosts.map((post, index) => {
            const isExpanded = expandedPosts.has(post.id);
            const shouldTruncate = post.content.length > 200;
            const displayContent = shouldTruncate && !isExpanded ? truncateContent(post.content) : post.content;
            
            // Count reactions by type
            const helpfulCount = post.reactions.filter(r => r.type === 'helpful').length;
            const heartCount = post.reactions.filter(r => r.type === 'heart').length;
            const thanksCount = post.reactions.filter(r => r.type === 'thanks').length;

            return (
              <div
                key={post.id}
                className="card-interactive p-4 animate-slide-up dark:bg-gray-800 dark:border-gray-700"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <div className="flex items-start space-x-3 mb-3">
                  {/* Clickable User Avatar */}
                  <button
                    onClick={() => !post.isAnonymous && handleUserProfileClick({
                      name: post.authorName,
                      isExpert: post.isExpert,
                      id: post.authorId
                    })}
                    className="avatar-md hover:scale-110 transition-transform duration-200"
                  >
                    {post.isAnonymous ? '?' : post.authorName.charAt(0)}
                  </button>
                  <div className="flex-1">
                    <div className="flex items-center space-x-2">
                      {/* Clickable Username */}
                      <button
                        onClick={() => !post.isAnonymous && handleUserProfileClick({
                          name: post.authorName,
                          isExpert: post.isExpert,
                          id: post.authorId
                        })}
                        className="font-medium text-gray-900 dark:text-gray-100 hover:text-green-600 dark:hover:text-green-400 transition-colors duration-200"
                      >
                        {post.isAnonymous ? 'Anonymous' : post.authorName}
                      </button>
                      {post.isExpert && !post.isAnonymous && (
                        <span className="badge-success animate-pulse">
                          ✓ Expert
                        </span>
                      )}
                      <span className="text-gray-500 dark:text-gray-400 text-sm">{post.timestamp}</span>
                      {isWellnessCircle && (
                        <span className="badge-info">
                          📍 Kumasi
                        </span>
                      )}
                    </div>
                    <p className="text-gray-600 dark:text-gray-400 text-sm capitalize">{post.category.replace('-', ' ')}</p>
                  </div>
                </div>
                
                {/* Post Content - Clickable to open discussion view */}
                <div 
                  className="mb-3 cursor-pointer"
                  onClick={() => handlePostDetailClick(post)}
                >
                  <p className="text-gray-800 dark:text-gray-200 leading-relaxed whitespace-pre-line">{displayContent}</p>
                  {shouldTruncate && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        togglePostExpansion(post.id);
                      }}
                      className="text-green-600 dark:text-green-400 hover:text-green-700 dark:hover:text-green-300 text-sm font-medium mt-2 transition-colors"
                    >
                      {isExpanded ? 'Show less' : 'Read more'}
                    </button>
                  )}
                  
                  {/* Display poll if this is a poll post */}
                  {post.postType === 'poll' && post.pollOptions && post.pollOptions.length > 0 && (
                    <PollDisplay post={post} />
                  )}
                </div>
                
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <ReactionButton type="helpful" count={helpfulCount} active={false} postId={post.id} />
                    <ReactionButton type="heart" count={heartCount} active={false} postId={post.id} />
                    <ReactionButton type="thanks" count={thanksCount} active={false} postId={post.id} />
                    <button 
                      onClick={() => handlePostDetailClick(post)}
                      className="flex items-center space-x-1 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 transition-all duration-200 hover:scale-105"
                    >
                      <MessageCircle size={16} />
                      <span className="text-sm">{post.comments.length}</span>
                    </button>
                  </div>
                  <div className="flex items-center space-x-2">
                    {post.comments.some(c => c.isExpertAnswer) && (
                      <span className="badge-success animate-pulse">
                        Expert Answered
                      </span>
                    )}
                    <button 
                      onClick={() => handleSharePost(post)}
                      className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-all duration-200 hover:scale-110"
                    >
                      <Share2 size={16} />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        
        {filteredPosts.length === 0 && (
          <div className="text-center py-12 animate-fade-in">
            <Users className="mx-auto text-gray-400 dark:text-gray-600 mb-4" size={48} />
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
              {isWellnessCircle ? 'No local discussions found' : 'No discussions found'}
            </h3>
            <p className="text-gray-600 dark:text-gray-300 mb-4">
              {isWellnessCircle 
                ? 'Be the first to start a conversation in your wellness circle!' 
                : 'Be the first to start a conversation!'
              }
            </p>
            <button
              onClick={handleCreatePostClick}
              className="btn-primary"
            >
              Start Discussion
            </button>
          </div>
        )}
      </div>

      {/* Modals and Views */}
      {showCreatePost && <CreatePostModal />}
      {showDiscussion && (
        <DiscussionView 
          post={posts.find(p => p.id === showDiscussion)!}
          onClose={() => setShowDiscussion(null)}
          onPostInteraction={onPostInteraction}
          onAddComment={async (postId, content) => {
            // In a real app, this would call an API to add a comment
            console.log('Adding comment to post', postId, content);
            // For demo purposes, we'll just show a toast
            showToast.community.commentAdded();
            return Promise.resolve();
          }}
          onFollowExpert={onFollowExpert}
          onSharePost={handleSharePost}
          onReportPost={(postId, reason) => {
            console.log('Reporting post', postId, reason);
            showToast.community.postReported();
          }}
          onViewProfile={handleUserProfileClick}
        />
      )}
      {selectedUser && <UserProfilePopover user={selectedUser} />}
    </div>
  );
};

export default CommunityEchoes;